import { gql } from "@apollo/client";

import { LIST_CONNECTION_REQUESTS } from "../services/connectionRequest/constants";
import { LIST_CUSTOMER_PORTALS } from "../services/customerPortal/constants";

import {
  AiAssistant,
  Customer,
  CustomizeField,
  InventoryPart,
  Oem,
  PreventiveMaintenance,
  Ticket,
  TimeTracker,
  ProcedureTemplate,
  ProcedureInstance,
  User,
  Resource,
  Team,
  WorkOrderType,
  Reports,
  MachineHistory,
  ActivityLog,
  TicketScheduleDraft,
  UserSchedule,
  AiNote,
  AssetTemplate,
  Asset,
  Document,
  AiAssistantChat,
  Schedule,
} from "~/api/_fragments";
import { LIST_CONTACTS } from "~/services/contact/constants";
import { GET_SHARED_ORGANIZATION_DETAILS } from "~/services/customer/constants";
import {
  GET_CITATION_HIGHLIGHT,
  GET_DOCUMENT_ANSWERS,
  GET_DOCUMENT_ANSWER_META,
  GET_DOCUMENT_AUTHORS,
  GET_DOCUMENT_BY_ID,
  GET_DOCUMENT_CHUNKS_BY_IDS,
  GET_DOCUMENT_TRANSLATION_HIGHLIGHT,
  LIST_DOCUMENTS,
  QUERY_DOCUMENTS,
} from "~/services/document/constants";
import { GET_KNOWLEDGE_BASE } from "~/services/knowledgeBase/constants";
import { GET_REQUEST, LIST_REQUESTS } from "~/services/request/constants";

export const GET_AI_ASSISTANT = "getAiAssistant";
export const GET_OWN_OEM = "getOwnOem";
export const LIST_OWN_OEM_PLANS = "listOwnOemPlans";
export const LIST_AI_ASSISTANTS = "listAiAssistants";
export const LIST_AI_ASSISTANT_CHATS = "listAiAssistantChats";
export const GET_AI_ASSISTANT_CHAT = "getAiAssistantChat";
export const LIST_ASSISTANT_DOCUMENTS = "listAssistantDocuments";
export const CURRENT_USER = "currentUser";
export const LIST_SHARED_ASSISTANTS = "listSharedAiAssistants";
export const GET_AI_ASSISTANT_USAGE = "getAiAssistantUsage";

// REMOTE
export const CONENCTION_QUERY = gql`
  query connection {
    connection
  }
`;

export const AUTH_QUERY = gql`
  query authenticate {
    _checkAuth
  }
`;

export const CURRENT_USER_QUERY = gql`
  query ${CURRENT_USER} {
    ${CURRENT_USER} {
      oem {
        _id
        brandLogo
        urlOem
        logo
        name
        slug
        paidFeatures
        currency
        allowHourlyPMEOption
        productCatalogEnabled
        autoLinkContactsByDomain
        allowFollowersMyWorkOrders
        allowAssigneesAcrossTeams
        allowTechnicianToAssignWorkOrders
        boxProxyEnabled
        emailAccountLinked
        emailAccounts {
          emailAccountStatus
          emailAddress
          isSynced
          _id
          needsReconnect
        }
        installedProducts {
          type
          tier
        }
        maximumAllowedEmailAddresses
        calendarSyncConfiguration {
          allowedConnectedAccounts
          consumedConnectedAccounts
        }
        skills {
          _id
          name
        }
        statuses {
          _id
          label
          color
        }
        supportTicketStatuses {
          _id
          label
          color
        }
        skills {
          _id
          name
        }
        ticketTypes {
          ...WorkOrderTypeData
        }
        supportTicketTypes {
          ...WorkOrderTypeData
        }
        usageTracking {
          logRocket
        }
        assetTypes {
          _id
          name
        }
        businessHours {
          dayOfWeek
          enabled
          from
          to
        }
      }
      skills {
        _id
      }
      teams {
        _id
        name
        teamColor
        disallowedTicketTypes {
          _id
        }
      }
      autoAppendEmailSignature
      personalEmailAccounts {
        emailAccountStatus
        emailAddress
        isSynced
        _id
        needsReconnect
      }
      personalMaximumAllowedEmailAddresses
      ...UserAuthData
    }
  }
  ${User.fragments.UserAuthData}
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

/**
 * Just what the user's role allows. Shares the user's cache entry with
 * CURRENT_USER_QUERY, so everything reading that query sees the new
 * permissions without fetching the rest of the user again.
 */
export const CURRENT_USER_PERMISSIONS_QUERY = gql`
  query currentUserPermissions {
    ${CURRENT_USER} {
      _id
      role
      roleId
      roleName
      permissions
    }
  }
`;
export const GET_CHAT_CREDENTIALS_QUERY = gql`
  query getChatCredentials {
    getChatCredentials {
      token
      uuid
      keys
      uuidMetadata
      callChannels
      notificationChannel
      organizationNotificationChannel
      notificationChannelGroupName
      requestsChannelsGroupNames
      supportNotificationChannelGroupName
      supportRequestsChannelsGroupNames
      # NOTE: requires the backend change that adds this field to
      # getChatCredentials. Deploy the backend first — an unknown field fails
      # validation for the whole query, which would leave chat with no
      # credentials at all.
      connectionNotificationChannels
      # Which channel group belongs to which connection. The group names above
      # are hashes of the connection id, so nothing about them says whose they
      # are — and the portal sidebar has to know, because a rerouted ticket's
      # channel is named after the company that raised it rather than the
      # portal that answers it.
      channelGroupsByConnection {
        connection
        requests
        support
      }
    }
  }
`;

export const GET_FOLDERS_ACCESS_TOKEN_QUERY = gql`
  query getFoldersAccessToken {
    getFoldersAccessToken
  }
`;

export const GET_INTERCOM_HASH_QUERY = gql`
  query getIntercomHash {
    getIntercomHash
  }
`;

export const GET_CURRENT_USER_AI_ASSISTANT_USAGE_QUERY = gql`
  query ${CURRENT_USER} {
    ${CURRENT_USER} {
      _id
      consumedRecordingSeconds
      aiAssistantConfiguration {
        consumedQueries
        consumedStorage
      }
    }
  }
`;

export const GET_AI_ASSISTANT_USAGE_QUERY = gql`
  query ${GET_AI_ASSISTANT_USAGE}($oemId: ID!) {
    ${GET_AI_ASSISTANT_USAGE}(oemId: $oemId) {
      allowedQueries
      consumedQueries
    }
  }
`;

export const GET_OEM_BY_QUERY = gql`
  query getOemBySlug($slug: String!) {
    getOemBySlug(slug: $slug) {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const GET_OWN_OEM_QUERY = gql`
  query ${GET_OWN_OEM} {
    ${GET_OWN_OEM} {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const LIST_OWN_OEM_PLANS_QUERY = gql`
  query ${LIST_OWN_OEM_PLANS} {
    ${LIST_OWN_OEM_PLANS} {
      type
      tier
      users
    }
  }
`;

export const GET_OWN_OEM_AI_ASSISTANT_USAGE_QUERY = gql`
  query ${GET_OWN_OEM} {
    ${GET_OWN_OEM} {
      ...AiAssistantUsage
      ...AiAssistantConfiguration
    }
  }
  ${Oem.fragments.AiAssistantConfiguration}
  ${Oem.fragments.AiAssistantUsage}
`;

export const LIST_OWN_OEM_ASSIGNEES_QUERY = gql`
  query listOwnOemSupportAccounts {
    listOwnOemSupportAccounts {
      _id
      name
    }
  }
`;

export const LIST_OWN_OEM_SUPPORT_ACCOUNTS_QUERY = gql`
  query listOwnOemSupportAccounts(
    $team: String
    $excludeTeam: String
    $withoutTeam: Boolean
    $getAllUsers: Boolean
    $useAssetTeams: Boolean
  ) {
    listOwnOemSupportAccounts(
      team: $team
      excludeTeam: $excludeTeam
      withoutTeam: $withoutTeam
      getAllUsers: $getAllUsers
      useAssetTeams: $useAssetTeams
    ) {
      productAccess
      ...UserBasicData
      status
    }
  }
  ${User.fragments.UserBasicData}
`;
export const LIST_OWN_OEM_SCHEDULER_AGENTS_QUERY = gql`
  query listOwnOemSchedulerAgents {
    listOwnOemSchedulerAgents {
      productAccess
      ...UserBasicData
      status
      deleted
      workingHours {
        dayOfWeek
        enabled
        from
        to
      }
      hasPersonalWorkingHours
    }
  }
  ${User.fragments.UserBasicData}
`;
export const LIST_ASSIGNABLE_USERS_QUERY = gql`
  query listAssignableUsers($ticketId: ID!) {
    listAssignableUsers(ticketId: $ticketId) {
      _id
      name
      email
      role
      teams {
        _id
        name
        teamColor
        members {
          _id
          name
        }
      }
    }
  }
`;

export const LIST_OWN_TICKET_MENTIONABLE_USERS_QUERY = gql`
  query listOwnTicketMentionableUsers($ticketId: ID!) {
    listOwnTicketMentionableUsers(ticketId: $ticketId) {
      oemUsers {
        _id
        name
        teams {
          _id
          name
          teamColor
        }
      }
      facilityUsers {
        _id
        name
        contactId
        contactName
        teams {
          _id
          name
          teamColor
        }
      }
    }
  }
`;

export const GET_OWN_OEM_CUSTOMER_BY_ID_QUERY = gql`
  query getOwnOemCustomerById($id: ID!) {
    getOwnOemCustomerById(id: $id) {
      ...CustomerFullData
    }
  }
  ${Customer.fragments.CustomerFullData}
`;

export const GET_OWN_OEM_CUSTOMER_MAP_DATA_BY_ID = gql`
  query getOwnOemCustomerById($id: ID!) {
    getOwnOemCustomerById(id: $id) {
      _id
      name
      facilityId
    }
  }
`;

export const LIST_OWN_OEM_FACILITY_USERS_QUERY = gql`
  query listOwnOemFacilityUsers($params: InputQueryParams) {
    listOwnOemFacilityUsers(params: $params) {
      currentPage
      limit
      skip
      totalCount
      users {
        ...UserBasicData
      }
    }
  }
  ${User.fragments.UserBasicData}
`;

export const LIST_OWN_OEM_CLOSED_TICKETS_QUERY = gql`
  query listOwnOemClosedTickets {
    listOwnOemClosedTickets {
      ...TicketListData
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_OWN_OEM_USER_CLOSED_TICKETS_QUERY = gql`
  query listOwnOemUserClosedTickets {
    listOwnOemUserClosedTickets {
      ...TicketListData
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_OWN_OEM_OPEN_TICKETS_QUERY = gql`
  query listOwnOemOpenTickets($params: InputQueryParams) {
    listOwnOemOpenTickets(params: $params) {
      ...TicketListData
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_OWN_OEM_ALL_TICKETS_QUERY = gql`
  query listOwnOemAllTickets($params: InputQueryParams) {
    listOwnOemAllTickets(params: $params) {
      ...TicketListData
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_OWN_OEM_TICKETS_QUERY = gql`
  query listOwnOemTickets($params: InputQueryParams) {
    listOwnOemTickets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      tickets {
        ...TicketListData
      }
    }
  }
  ${Ticket.fragments.TicketListData}
`;

/** Lightweight ids fetch for bulk "select all matching" — avoids TicketListData payloads. */
export const LIST_OWN_OEM_TICKET_IDS_QUERY = gql`
  query listOwnOemTicketIds($params: InputQueryParams) {
    listOwnOemTickets(params: $params) {
      totalCount
      tickets {
        _id
        subWorkOrders {
          _id
        }
      }
    }
  }
`;

export const LIST_OWN_OEM_ARCHIVED_TICKETS_QUERY = gql`
  query listOwnOemArchivedTickets($params: InputQueryParams) {
    listOwnOemArchivedTickets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      tickets {
        ...TicketListData
      }
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_OWN_OEM_UNSCHEDULED_TICKETS_QUERY = gql`
  query listOwnOemTickets($params: InputQueryParams) {
    listOwnOemTickets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      tickets {
        _id
        ticketId
        title
        description
        status
        ticketType {
          name
          _id
        }
        assets {
          _id
          name
          serialNumber
        }
        subWorkOrders {
          _id
        }
        assignees {
          _id
          name
        }
        teams {
          _id
          name
          teamColor
        }
        facility {
          name
        }
        ...TicketScheduleData
      }
    }
  }
  ${Schedule.fragments.TicketScheduleData}
`;

export const LIST_OWN_OEM_SCHEDULED_TICKETS_QUERY = gql`
  query listOwnOemTickets($params: InputQueryParams) {
    listOwnOemTickets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      tickets {
        _id
        ticketId
        title
        description
        status
        dueDate
        ticketType {
          _id
          name
          icon
          color
          isInternal
        }
        assignees {
          _id
        }
        facility {
          _id
          name
        }
        ...TicketScheduleData
      }
    }
  }
  ${Schedule.fragments.TicketScheduleData}
`;

export const LIST_GROUPED_TICKETS_QUERY = gql`
  query listGroupedByCustomersTickets($params: InputQueryParams) {
    listGroupedByCustomersTickets(params: $params) {
      customerId
      tickets {
        ...TicketListData
      }
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_CUSTOMERS_WITH_TICKETS = gql`
  query listCustomersWithTickets($params: InputQueryParams) {
    listCustomersWithTickets(params: $params)
  }
`;

export const LIST_CUSTOMERS_WITH_SUPPORT_TICKETS = gql`
  query listCustomersWithSupportTickets($params: InputQueryParams) {
    listCustomersWithSupportTickets(params: $params)
  }
`;

export const LIST_OWN_OEM_ASSET_HIERARCHY_TICKETS = gql`
  query listOwnAssetHierarchyTickets($params: InputQueryParams) {
    listOwnAssetHierarchyTickets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      tickets {
        ...TicketListData
      }
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_REQUESTS_QUERY = gql`
  query ${LIST_REQUESTS}($params: InputQueryParams) {
    ${LIST_REQUESTS}(params: $params) {
      currentPage
      limit
      skip
      totalCount
      requests {
        ...TicketListData
      }
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const LIST_OWN_OEM_KANBAN_TICKETS = gql`
  query listOwnOemKanbanTickets($params: InputQueryParams) {
    listOwnOemKanbanTickets(params: $params) {
      totalCount
      columns {
        _id
        boardFor
        type
        customField
        column
        status {
          _id
          label
          color
        }
        columnIndex
        cards {
          _id
          ticketId
          dueDate
          title
          status
          ticketChatChannels
          ticketInternalNotesChatChannels
          ticketType {
            _id
            name
            icon
            color
            isInternal
            isSystem
          }
          subWorkOrders {
            _id
          }
          createdAt
          createdBy {
            _id
            name
          }
          updatedAt
          assignees {
            _id
            name
          }
          followers {
            _id
          }
          machine {
            _id
            name
            serialNumber
            isDeleted
          }
          assets {
            _id
            name
            serialNumber
            isDeleted
          }
          facility {
            _id
            name
            isDeleted
          }
          ...TicketScheduleData
        }
        totalCount
        limit
        skip
        currentPage
      }
    }
  }
  ${Schedule.fragments.TicketScheduleData}
`;

export const LIST_OEM_CUSTOMERS_IN_AREA = gql`
  query listOemCustomersInArea($params: InputQueryParams) {
    listOemCustomersInArea(params: $params) {
      customerAddress {
        coordinates
      }
      _id
    }
  }
`;

export const LIST_OWN_OEM_USER_OPEN_TICKETS_QUERY = gql`
  query listOwnOemUserOpenTickets {
    listOwnOemUserOpenTickets {
      ...TicketListData
    }
  }
  ${Ticket.fragments.TicketListData}
`;

export const GET_OWN_OEM_TICKET_BY_ID = gql`
  query getOwnOemTicketById($id: ID!) {
    getOwnOemTicketById(id: $id) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const GET_OWN_OEM_SCHEDULER_TICKET_BY_ID = gql`
  query getOwnOemTicketById($id: ID!) {
    getOwnOemTicketById(id: $id) {
      _id
      ticketId
      machine {
        _id
        name
        serialNumber
        isDeleted
      }
      assets {
        _id
        name
        serialNumber
        isDeleted
      }
      teams {
        _id
        name
        teamColor
      }
      facility {
        _id
        name
        isDeleted
      }
      assignees {
        _id
        name
      }
      status
      resources {
        _id
      }
    }
  }
`;

export const GET_REQUEST_QUERY = gql`
  query ${GET_REQUEST}($requestId: ID!) {
    ${GET_REQUEST}(requestId: $requestId) {
      ...TicketSingleData
      inventoryParts {
        showOnPortal
      }
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const LIST_ALL_OEM_CUSTOMERS_IN_AREA = gql`
  query listAllOemCustomersInArea($params: InputQueryParams) {
    listAllOemCustomersInArea(params: $params) {
      customerAddress {
        coordinates
      }
      _id
      name
      facilityIdentifier
    }
  }
`;

export const LIST_ADDITIONAL_FIELDS_QUERY = gql`
  query listAdditionalFields($type: Types) {
    listAdditionalFields(type: $type) {
      ...AdditionFieldData
    }
  }
  ${CustomizeField.fragments.AdditionFieldData}
`;

export const LIST_OWN_OEM_CUSTOM_ADDITIONAL_FIELDS_QUERY = gql`
  query listOwnOemCustomFields($type: Types) {
    listOwnOemCustomFields(type: $type) {
      ...CustomAdditionFieldData
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;

export const LIST_OWN_OEM_CUSTOM_FIELDS_FOR_PROCEDURE_HEADERS_QUERY = gql`
  query listOwnOemCustomFieldsForProcedureHeaders(
    $type: Types
    $procedureId: ID!
  ) {
    listOwnOemCustomFieldsForProcedureHeaders(
      type: $type
      procedureId: $procedureId
    ) {
      ...CustomAdditionFieldData
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;

export const LIST_PREVENTIVE_MAINTENANCE_EVENTS = gql`
  query listPreventiveMaintenanceEvents($params: InputQueryParams!) {
    listPreventiveMaintenanceEvents(params: $params) {
      ...PreventiveEventData
    }
  }
  ${PreventiveMaintenance.fragments.PreventiveEventData}
`;

export const LIST_SHARED_PREVENTIVE_MAINTENANCE_EVENTS = gql`
  query listSharedPreventiveMaintenanceEvents($params: InputQueryParams!) {
    listSharedPreventiveMaintenanceEvents(params: $params) {
      ...PreventiveEventData
    }
  }
  ${PreventiveMaintenance.fragments.PreventiveEventData}
`;

export const LIST_ALL_PREVENTIVE_MAINTENANCE_EVENTS = gql`
  query listAllPreventiveMaintenanceEvents($params: InputQueryParams!) {
    listAllPreventiveMaintenanceEvents(params: $params) {
      totalCount
      events {
        ...PreventiveEventDetailedData
      }
    }
  }
  ${PreventiveMaintenance.fragments.PreventiveEventDetailedData}
`;

export const GET_PREVENTIVE_MAINTENANCE_EVENT_BY_ID = gql`
  query getPreventiveMaintenanceEventByID($id: ID!) {
    getPreventiveMaintenanceEventByID(id: $id) {
      ...PreventiveEventData
    }
  }
  ${PreventiveMaintenance.fragments.PreventiveEventData}
`;

export const GET_OWN_OEM_REPORT_BY_ID_QUERY = gql`
  query getOwnOemReportByID($id: ID!) {
    getOwnOemReportByID(id: $id) {
      _id
      title
      createdBy
      entity
      segment {
        label
        value
      }
      xAxis {
        label
        value
      }
      table {
        label
        value
      }
      chartType
      filters {
        filterType
        field
        condition
        value {
          label
          value
        }
      }
      inputs {
        xAxis {
          label
          value
        }
        segment {
          label
          value
        }
        filter {
          filterType
          field
          condition
          value {
            label
            value
          }
        }
        table {
          label
          value
        }
      }
    }
  }
`;

export const LIST_OEM_INVENTORY_PARTS_QUERY = gql`
  query ListOwnOemInventoryPart($params: InputInventoryQueryParams) {
    listOwnOemInventoryPart(params: $params) {
      currentPage
      limit
      skip
      totalCount
      parts {
        ...InventoryPartBasicData
      }
    }
  }
  ${InventoryPart.fragments.InventoryPartBasicData}
`;

export const GET_INVENTORY_PART = gql`
  query getInventoryPart($id: ID!) {
    getInventoryPart(id: $id) {
      ...InventoryPartFullData
    }
  }
  ${InventoryPart.fragments.InventoryPartFullData}
`;

export const GET_INVENTORY_PART_DETAIL = gql`
  query GetInventoryPartDetail($id: ID!) {
    inventoryPart(id: $id) {
      _id
      name
      visibilityScope
      articleNumber
      description
      totalStock
      availableStock
      consumedStock
      totalCost
      price
      oem {
        _id
        name
      }
      machines {
        _id
        name
      }
      tickets {
        _id
        title
      }
      image
      thumbnail
      customFields {
        _id
        fieldId {
          _id
          name
          type
        }
        value
      }
      documentFolders {
        _id
        internalId
        externalId
      }
      createdBy
      createdAt
      accessToken
    }
  }
`;

export const LIST_STOCK_OPERATIONS_QUERY = gql`
  query ListStockOperations($params: InputStockOperationsQuery!) {
    listStockOperations(params: $params) {
      totalCount
      limit
      skip
      currentPage
      operations {
        _id
        operationType
        quantity
        costPerPart
        comment
        allocationStatus
        ticket {
          id
          ticketId
          title
        }
        ticketDetails {
          _id
          ticketId
          title
          status
          ticketType {
            _id
            icon
            color
            isInternal
          }
          machine {
            _id
            name
          }
          facility {
            _id
            name
          }
        }
        createdBy {
          _id
          firstName
          lastName
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const LIST_OWN_OEM_TIME_TRACKERS_QUERY = gql`
  query ListOwnOemTimeTrackers($type: Types!, $fieldType: FieldTypes!) {
    listOwnOemTimeTrackers(type: $type, fieldType: $fieldType) {
      ...TimeTrackerBasicData
    }
  }
  ${TimeTracker.fragments.TimeTrackerBasicData}
`;

export const LIST_OWN_OEM_CUSTOMERS_MISSING_CLIENT_IDS_QUERY = gql`
  query listOwnOemCustomersMissingClientIds($clientIds: [String!]!) {
    listOwnOemCustomersMissingClientIds(clientIds: $clientIds)
  }
`;

export const LIST_OWN_OEM_DUPLICATE_TICKETS = gql`
  query listOwnOemDuplicateTickets($ticketIds: [String!]!) {
    listOwnOemDuplicateTickets(ticketIds: $ticketIds)
  }
`;

export const LIST_OWN_OEM_DUPLICATE_SUPPORT_TICKETS = gql`
  query listOwnOemDuplicateSupportTickets($supportTicketIds: [String!]!) {
    listOwnOemDuplicateSupportTickets(supportTicketIds: $supportTicketIds)
  }
`;

export const LIST_ASSETS_WITH_DIFFERENT_LINKED_TEMPLATES = gql`
  query listAssetsWithDifferentLinkedTemplates(
    $assets: [InputAssetLinkedTemplate!]!
  ) {
    listAssetsWithDifferentLinkedTemplates(assets: $assets)
  }
`;

export const LIST_OWN_OEM_PROCEDURE_TEMPLATES_MISSING_IDS_QUERY = gql`
  query listOwnOemProcedureTemplatesMissingIds(
    $procedureTemplateIds: [String!]!
  ) {
    listOwnOemProcedureTemplatesMissingIds(
      procedureTemplateIds: $procedureTemplateIds
    )
  }
`;

export const CHECK_PARENT_CLIENT_IDS_QUERY = gql`
  query checkParentClientIds($parentClientIds: [String!]!) {
    checkParentClientIds(parentClientIds: $parentClientIds)
  }
`;

export const CHECK_PARENT_TICKET_IDS_QUERY = gql`
  query checkParentTicketIds($parentTicketIds: [String!]!) {
    checkParentTicketIds(parentTicketIds: $parentTicketIds)
  }
`;

export const VALIDATE_PARENT_TICKET_ASSETS_QUERY = gql`
  query validateParentTicketAssets(
    $records: [ValidateParentTicketAssetInput!]!
  ) {
    validateParentTicketAssets(records: $records) {
      ticketId
      errors
    }
  }
`;

export const LIST_OWN_OEM_PARTS_INVALID_ARTICLE_NUMBERS_FOR_ASSET_QUERY = gql`
  query listOwnOemPartsInvalidArticleNumbersForAsset(
    $articleNumbers: [String!]!
    $assetSerialNumber: String!
  ) {
    listOwnOemPartsInvalidArticleNumbersForAsset(
      articleNumbers: $articleNumbers
      assetSerialNumber: $assetSerialNumber
    )
  }
`;

export const LIST_OEM_PROCEDURE_TEMPLATES_QUERY = gql`
  query listOwnOemProcedureTemplatesWithCount($params: InputQueryParams!) {
    listOwnOemProcedureTemplatesWithCount(params: $params) {
      procedures {
        _id
        name
        createdAt
        updatedAt
        childrenCount
        isInternal
        isAssetAssociated
        associatedAssetName
        createdBy {
          _id
          name
        }
      }
      totalCount
    }
  }
`;

export const LIST_ALL_OWN_OEM_REPORTS_QUERY = gql`
  query listAllOwnOemReports($params: InputQueryParams) {
    listAllOwnOemReports(params: $params) {
      currentPage
      limit
      skip
      totalCount
      reports {
        ...ReportData
      }
    }
  }
  ${Reports.fragments.ReportData}
`;

export const GET_OWN_OEM_PROCEDURE_TEMPLATE = gql`
  query GetOwnOemProcedureTemplate($id: String) {
    getOwnOemProcedureTemplate(id: $id) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;
export const GET_OWN_OEM_PROCEDURE_INSTANCE = gql`
  query getOwnOemProcedureById($id: ID!) {
    getOwnOemProcedureById(id: $id) {
      ...ProcedureInstanceFullData
    }
  }
  ${ProcedureInstance.fragments.ProcedureInstanceFullData}
`;

export const DOWNLOAD_PROCEDURE_PDF_BY_ID = gql`
  query downloadProcedurePDF($id: ID!, $uuid: String!, $timezone: String) {
    downloadProcedurePDF(id: $id, uuid: $uuid, timezone: $timezone)
  }
`;

export const GET_PROCEDURE_PDF_DOWNLOAD_STATUS = gql`
  query getProcedurePDFDownloadStatus($jobId: String!) {
    getProcedurePDFDownloadStatus(jobId: $jobId) {
      jobId
      status
      url
      error
    }
  }
`;

export const LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY = gql`
  query listDetachedProcedures($params: InputQueryParams!) {
    listDetachedProcedures(params: $params) {
      procedures {
        ...ProcedureInstanceFullData
      }
      totalCount
    }
  }
  ${ProcedureInstance.fragments.ProcedureInstanceFullData}
`;

export const GET_NON_FINALIZED_PROCEDURES_WITH_TEMPLATE = gql`
  query GetNonFinalizedProceduresWithTemplate($procedureTemplateId: ID!) {
    getNonFinalizedProceduresWithTemplate(
      procedureTemplateId: $procedureTemplateId
    ) {
      procedureTemplateId
      count
    }
  }
`;

export const DOWNLOAD_WO_PDF_BY_ID = gql`
  query downloadWOPDF($input: InputDownloadWOPDF) {
    downloadWOPDF(input: $input)
  }
`;

export const GET_APP_CONFIG = gql`
  query getAppConfig {
    getAppConfig {
      _id
      features
      plans {
        type
        name
        tiers {
          type
          name
          price
          allowedFeatures
          limits {
            type
            value
          }
        }
        conflictingPurchases
      }
    }
  }
`;

export const LIST_ALL_OWN_OEM_CUSTOMERS_QUERY = gql`
  query listAllOwnOemCustomers($params: InputQueryParams) {
    listAllOwnOemCustomers(params: $params) {
      currentPage
      limit
      skip
      totalCount
      customers {
        ...CustomerBasicData
        hasCustomerPortal
        parentsWithPortalCount
        totalMachines
      }
    }
  }
  ${Customer.fragments.CustomerBasicData}
`;

export const LIST_OWN_OEM_RESOURCES_QUERY = gql`
  query listOwnOemResources($params: InputQueryParams) {
    listOwnOemResources(params: $params) {
      currentPage
      limit
      resources {
        ...ResourceListData
      }
      skip
      totalCount
    }
  }
  ${Resource.fragments.ResourceListData}
`;

export const GET_OWN_OEM_TICKETS_BY_STATUS = gql`
  query checkTicketHasStatus($statusId: ID!) {
    checkTicketHasStatus(statusId: $statusId)
  }
`;

export const LIST_OWN_OEM_KANBAN_CUSTOMERS = gql`
  query listOwnOemKanbanCustomers($params: InputQueryParams) {
    listOwnOemKanbanCustomers(params: $params) {
      totalCount
      columns {
        _id
        boardFor
        type
        customField
        column
        columnIndex
        cards {
          ...CustomerBasicData
        }
        totalCount
        limit
        skip
        currentPage
      }
    }
  }
  ${Customer.fragments.CustomerBasicData}
`;

export const GET_OWN_OEM_ANALYTICS = gql`
  query getOwnOemAnalytics($input: InputGetAnalytics!) {
    getOwnOemAnalytics(input: $input) {
      data
      inputTableOptions {
        value
        label
      }
    }
  }
`;

export const LIST_TEAMS_QUERY = gql`
  query listTeams($params: InputQueryParams) {
    listTeams(params: $params) {
      currentPage
      limit
      skip
      totalCount
      teams {
        _id
        name
        description
        numberOfMembers
        teamColor
      }
    }
  }
`;

export const GET_TEAM_QUERY = gql`
  query getTeam($id: ID!) {
    getTeam(id: $id) {
      ...TeamData
    }
  }
  ${Team.fragments.TeamData}
`;

export const GET_OWN_OEM_TICKETS_BY_TYPES_QUERY = gql`
  query checkIfTicketsExistForType($ticketTypeId: ID!) {
    checkIfTicketsExistForType(ticketTypeId: $ticketTypeId)
  }
`;

// NOTE: unlike the work order query above, this resolver names its argument
// `typeId`, not `ticketTypeId`.
export const GET_OWN_OEM_SUPPORT_TICKETS_BY_TYPES_QUERY = gql`
  query checkIfSupportTicketsExistForType($typeId: ID!) {
    checkIfSupportTicketsExistForType(typeId: $typeId)
  }
`;

export const GET_SINGLE_LIST_TICKET_BY_ID_QUERY = gql`
  query getOwnOemTicketById($id: ID!) {
    getOwnOemTicketById(id: $id) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const LIST_OWN_OEM_MACHINE_HISTORY = gql`
  query listOwnOemMachineHistory(
    $params: InputQueryParams!
    $includeHierarchy: Boolean
    $selectedMachineIds: [ID!]
  ) {
    listOwnOemMachineHistory(
      params: $params
      includeHierarchy: $includeHierarchy
      selectedMachineIds: $selectedMachineIds
    ) {
      totalCount
      skip
      limit
      currentPage
      ticketCount
      supportTicketCount
      history {
        ...MachineHistoryData
      }
      hierarchyAssets {
        _id
        name
        level
      }
    }
  }
  ${MachineHistory.fragments.MachineHistoryData}
`;

export const LIST_OWN_OEM_CONNECTION_HISTORY = gql`
  query listOwnOemConnectionHistory($params: InputQueryParams!) {
    listOwnOemConnectionHistory(params: $params) {
      totalCount
      skip
      limit
      currentPage
      machineCount
      ticketCount
      supportTicketCount
      history {
        ...MachineHistoryData
      }
    }
  }
  ${MachineHistory.fragments.MachineHistoryData}
`;

/**
 * One portal's own History tab.
 *
 * Separate from `LIST_OWN_OEM_CONNECTION_HISTORY` rather than a variant of it:
 * the company feed is scoped to the assets that company OWNS, which would hide
 * every sub portal ticket, since a reshared asset keeps belonging to the
 * company that handed it down. `machineCount` is not selected — this feed is
 * not scoped by asset, so the portal has no count to report.
 *
 * Suffixed `_QUERY` like the other portal documents, so it does not collide
 * with the resolver-key constant of the same name.
 */
export const LIST_OWN_OEM_PORTAL_HISTORY_QUERY = gql`
  query listOwnOemPortalHistory($params: InputQueryParams!) {
    listOwnOemPortalHistory(params: $params) {
      totalCount
      skip
      limit
      currentPage
      ticketCount
      supportTicketCount
      history {
        ...MachineHistoryData
      }
    }
  }
  ${MachineHistory.fragments.MachineHistoryData}
`;

export const LIST_OWN_OEM_SHARED_ASSET_HISTORY = gql`
  query listOwnOemSharedAssetHistory(
    $params: InputQueryParams!
    $includeHierarchy: Boolean
    $selectedMachineIds: [ID!]
  ) {
    listOwnOemSharedAssetHistory(
      params: $params
      includeHierarchy: $includeHierarchy
      selectedMachineIds: $selectedMachineIds
    ) {
      totalCount
      skip
      limit
      currentPage
      ticketCount
      supportTicketCount
      history {
        ...MachineHistoryData
        # A portal reads the history of every asset it holds, including rows for
        # tickets raised by other portals on the share chain, but it can only
        # open the ticket that routes to it. Selected here rather than on the
        # shared fragment: the OEM's own history queries always resolve it to
        # true, so only this query needs it.
        isResourceAccessible
      }
      hierarchyAssets {
        _id
        name
        level
      }
    }
  }
  ${MachineHistory.fragments.MachineHistoryData}
`;

export const LIST_OWN_OEM_ACTIVITY_LOGS = gql`
  query listOwnOemActivityLogs($params: InputQueryParams) {
    listOwnOemActivityLogs(params: $params) {
      totalCount
      skip
      limit
      currentPage
      logs {
        ...ActivityLogData
      }
    }
  }
  ${ActivityLog.fragments.ActivityLogData}
`;

export const GET_ACTIVITY_LOG_SNAPSHOT_QUERY = gql`
  query activityLogSnapshot($activityLogId: ID!) {
    activityLogSnapshot(activityLogId: $activityLogId)
  }
`;

export const GET_DATA_CSV_QUERY = gql`
  query getDataCsv($params: InputQueryParams!) {
    getDataCsv(params: $params)
  }
`;

export const LIST_ASSISTANT_DOCUMENTS_QUERY = gql`
  query ${LIST_ASSISTANT_DOCUMENTS}($assistantId: ID!, $params: InputQueryParams) {
    ${LIST_ASSISTANT_DOCUMENTS}(assistantId: $assistantId, params: $params) {
      totalCount
      skip
      limit
      currentPage
      documents {
        _id
        aiGeneratedTitle
        boxDocumentId
        size
        stage
        stageProgress
        status
        statusReason
        title
        visibility
      }
    }
  }
`;

// Unpaginated, ids only — feeds the file picker's exclude list (no params ⇒
// the backend returns every document owned by the assistant).
export const LIST_ASSISTANT_DOCUMENT_IDS_QUERY = gql`
  query listAssistantDocumentIds($assistantId: ID!) {
    ${LIST_ASSISTANT_DOCUMENTS}(assistantId: $assistantId) {
      documents {
        _id
        boxDocumentId
      }
    }
  }
`;

export const GET_AI_ASSISTANT_QUERY = gql`
  query ${GET_AI_ASSISTANT}($id: ID!) {
    ${GET_AI_ASSISTANT}(id: $id) {
      ...BasicData
      isIndexAllScheduled
    }
  }
  ${AiAssistant.BasicData}
`;

export const LIST_AI_ASSISTANTS_QUERY = gql`
  query ${LIST_AI_ASSISTANTS}($limit: Int, $searchQuery: String) {
    ${LIST_AI_ASSISTANTS}(limit: $limit, searchQuery: $searchQuery) {
      aiAssistants {
        ...BasicData
      }
      totalCount
    }
  }
  ${AiAssistant.BasicData}
`;

export const LIST_SHARED_AI_ASSISTANTS_QUERY = gql`
  query ${LIST_SHARED_ASSISTANTS}($params: InputQueryParams!) {
    ${LIST_SHARED_ASSISTANTS}(params: $params) {
      totalCount
      skip
      limit
      currentPage
      assistants {
        ...BasicData
        isSharedAssistant
        oemDetails {
          name
          website
          logo
          brandLogo
        }
      }
    }
  }
  ${AiAssistant.BasicData}
`;

export const GET_AI_ASSISTANT_CHAT_QUERY = gql`
  query ${GET_AI_ASSISTANT_CHAT}($id: ID!) {
    ${GET_AI_ASSISTANT_CHAT}(id: $id) {
      ...AiAssistantChatData
    }
  }
  ${AiAssistantChat.fragments.FullData}
`;

export const LIST_AI_ASSISTANT_CHATS_QUERY = gql`
  query ${LIST_AI_ASSISTANT_CHATS}($params: InputQueryParams!) {
    ${LIST_AI_ASSISTANT_CHATS}(params: $params) {
      totalCount
      skip
      limit
      currentPage
      aiAssistantChats {
        ...BasicData
      }
    }
  }
  ${AiAssistantChat.fragments.BasicData}
`;

export const GET_OWN_TICKET_SCHEDULE_DRAFT_QUERY = gql`
  query getOwnTicketScheduleDraft {
    getOwnTicketScheduleDraft {
      ...TicketScheduleDraftData
    }
  }
  ${TicketScheduleDraft.fragments.TicketScheduleDraftData}
`;

export const LIST_OWN_USER_SCHEDULES_QUERY = gql`
  query listOwnUserSchedules($params: InputQueryParams) {
    listOwnUserSchedules(params: $params) {
      currentPage
      limit
      skip
      totalCount
      userSchedules {
        ...UserScheduleData
      }
    }
  }
  ${UserSchedule.fragments.UserScheduleData}
`;

export const GET_CALENDAR_SYNC_AUTH_LINK_QUERY = gql`
  query getCalendarSyncAuthUrl {
    getCalendarSyncAuthUrl
  }
`;

export const LIST_OWN_CALENDAR_EVENTS_QUERY = gql`
  query listUsersCalendarEvents($startDate: DateTime!, $endDate: DateTime!) {
    listUsersCalendarEvents(startDate: $startDate, endDate: $endDate) {
      id
      when
      assignees
    }
  }
`;

export const GET_SHARED_ORGANIZATION_DETAILS_QUERY = gql`
  query ${GET_SHARED_ORGANIZATION_DETAILS}($connectionId: ID!, $isPreview: Boolean) {
    ${GET_SHARED_ORGANIZATION_DETAILS}(connectionId: $connectionId, isPreview: $isPreview) {
        oem {
          ...OemBasicData
          assetTypes {
            name
            _id
          }
          paidFeatures
        }
        assetAccess {
          documentation
          parts
          preventiveMaintenance
          qrCodes
          history
        }
        portal {
          _id
          name
          allowSubPortalCreation
          connection {
            _id
          }
          hierarchy {
            id
          }
        }
        isMember
        managingParent {
          connectionId
          portalId
        }
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const GET_OWN_AI_NOTE_QUERY = gql`
  query getOwnAiNote($id: ID!) {
    getOwnAiNote(id: $id) {
      ...AiNoteData
    }
  }
  ${AiNote.fragments.AiNoteData}
`;

export const GET_AI_NOTE_QUERY = gql`
  query getOwnAiNote($id: ID!) {
    getOwnAiNote(id: $id) {
      ...AiNotePublicData
    }
  }
  ${AiNote.fragments.AiNotePublicData}
`;

export const LIST_OWN_AI_NOTES_QUERY = gql`
  query listOwnAiNotes($params: InputQueryParams) {
    listOwnAiNotes(params: $params) {
      currentPage
      limit
      skip
      totalCount
      aiNotes {
        ...AiNoteBasicData
      }
    }
  }
  ${AiNote.fragments.AiNoteBasicData}
`;

export const GET_OWN_OEM_ASSET_TEMPLATE_BY_ID = gql`
  query getOwnOemAssetTemplate($templateId: ID!) {
    getOwnOemAssetTemplate(templateId: $templateId) {
      ...AssetTemplateFullData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateFullData}
`;

export const GET_CUSTOMER_PORTAL_QUERY = gql`
  query getCustomerPortal($id: ID!) {
    getCustomerPortal(id: $id) {
      name
      _id
      childrenCount
      totalChildrenCount
      totalPortalCount
      parents {
        id
      }
      connection {
        _id
        name
      }
      allowInvitingMembers
      allowRequestCreation
      # The portal's effective permission, true while ANY grantor grants it.
      allowSubPortalCreation
      # The OEM's OWN grant, which is what the toggle on this page edits. It
      # governs the assets the OEM gave this portal; a parent portal's grant on
      # the same portal is separate and governs the ones that parent handed
      # down, so the two can disagree and neither overwrites the other.
      subPortalCreation
      # False when no parent of this portal holds the permission, so it cannot
      # be granted this deep — it reaches down one level at a time. Always true
      # for a root portal.
      canEnableSubPortalCreation
      # The OEM's own reroute grant, which is what the settings toggle reads
      # back. Independent of any parent portal's grant on the same portal.
      ticketReroute
      childPortalAssetVisibility
      allowSupportTicketCreation

      assetAccess {
        documentation
        parts
        preventiveMaintenance
        qrCodes
        history
      }
      createdBy {
        _id
      }
      # Closes the History tab's feed, the way a company's creation date closes
      # its own.
      createdAt
      contacts {
        _id
        name
        email
        deleted
        jobTitle
        landline
        phoneNumber
        oem
        user
        connection {
          _id
          name
        }
        accessStatus
        canResendInvite
        createdBy {
          _id
        }
      }
      sharedDrive {
        _id
        internalId
        externalId
      }
      productAccess {
        allowCopying
        documentation
        parts
        procedures
      }
    }
  }
`;

export const GET_SHARED_ASSET_TEMPLATE_BY_ID = gql`
  query getOwnOemAssetTemplate($templateId: ID!) {
    getOwnOemAssetTemplate(templateId: $templateId) {
      ...AssetTemplateFullData
      sharedBoxToken
      productAccess {
        allowCopying
        documentation
        parts
        procedures
      }
    }
  }
  ${AssetTemplate.fragments.AssetTemplateFullData}
`;

export const LIST_OWN_OEM_ASSET_TEMPLATE_QUERY = gql`
  query listOwnOemAssetTemplates($params: InputQueryParams!) {
    listOwnOemAssetTemplates(params: $params) {
      ...AssetTemplateData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateData}
`;

export const LIST_OWN_OEM_ASSET_TEMPLATE_WITH_COUNT_QUERY = gql`
  query listOwnOemAssetTemplatesWithCount($params: InputQueryParams!) {
    listOwnOemAssetTemplatesWithCount(params: $params) {
      templates {
        ...AssetTemplateData
      }
      totalCount
    }
  }
  ${AssetTemplate.fragments.AssetTemplateData}
`;

export const LIST_SHARED_OEM_ASSET_TEMPLATE_QUERY = gql`
  query listSharedAssetTemplates($params: InputQueryParams!) {
    listSharedAssetTemplates(params: $params) {
      totalCount
      skip
      limit
      currentPage
      templates {
        ...AssetTemplateData
      }
    }
  }
  ${AssetTemplate.fragments.AssetTemplateData}
`;

export const GET_OWN_OEM_ASSET_BY_ID_QUERY = gql`
  query getOwnOemAssetById($id: ID!, $isSharedAsset: Boolean) {
    getOwnOemAssetById(id: $id, isSharedAsset: $isSharedAsset) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const GET_SHARED_OEM_ASSET_BY_ID_QUERY = gql`
  query getOwnOemAssetById(
    $id: ID!
    $isSharedAsset: Boolean
    $connectionId: ID
  ) {
    getOwnOemAssetById(id: $id, isSharedAsset: $isSharedAsset) {
      ...AssetFullData
      # Which portal is being viewed decides the answer: a reshared asset
      # belongs to the company that passed it down, so the same asset resolves
      # differently in a parent portal and in its child.
      sharedAssetAccess(connectionId: $connectionId) {
        documentation
        parts
        preventiveMaintenance
        qrCodes
        history
      }
      sharedAssistant {
        _id
        assistantName
      }
      oem {
        _id
        name
        slug
        statuses {
          _id
          label
          color
        }
        assetTypes {
          _id
          name
        }
        ticketTypes {
          ...WorkOrderTypeData
        }
        website
        backgroundColor
        textColor
        logo
        thumbnail
        brandLogo
        coverPhoto
        slug
        paidFeatures
        installedProducts {
          type
          tier
        }
      }
    }
  }
  ${Asset.fragments.AssetFullData}
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

export const GET_OWN_OEM_ASSET_PARTS_BY_ID_QUERY = gql`
  query getOwnOemAssetById($id: ID!, $isSharedAsset: Boolean) {
    getOwnOemAssetById(id: $id, isSharedAsset: $isSharedAsset) {
      ...AssetPartsData
    }
  }
  ${Asset.fragments.AssetPartsData}
`;

export const GET_BOX_FOLDER_ACCESS_TOKEN_QUERY = gql`
  query getBoxFolderAccessToken($assetId: ID!) {
    getBoxFolderAccessToken(assetId: $assetId)
  }
`;

export const LIST_ALL_OWN_OEM_ASSETS_QUERY = gql`
  query listAllOwnOemAssets($params: InputQueryParams) {
    listAllOwnOemAssets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      assets {
        ...AssetListData
      }
    }
  }
  ${Asset.fragments.AssetListData}
`;

export const LIST_CUSTOMER_PORTAL_SHARED_ASSETS_QUERY = gql`
  query listOwnCustomerPortalSharedAssets($params: InputQueryParams) {
    # Aliased back to listAllOwnOemAssets so callers keep the same response
    # shape. The portal query is the access-gated one: it authorizes a
    # sub-asset listing by walking up to an ANCESTOR that is shared with the
    # portal, where listAllOwnOemAssets requires the caller to be a contact of
    # the parent asset's own company — which a reshared asset never is.
    listAllOwnOemAssets: listOwnCustomerPortalSharedAssets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      assets {
        ...AssetListData
      }
    }
  }
  ${Asset.fragments.AssetListData}
`;

export const LIST_OWN_OEM_ASSETS_CALENDAR_QUERY = gql`
  query listOwnOemAssetsCalendar($params: InputQueryParams) {
    listOwnOemAssetsCalendar(params: $params) {
      totalCount
      rootCount
      ticketsTotalCount
      assets {
        ...AssetListData
      }
      tickets {
        _id
        ticketId
        title
        status
        dueDate
        assets {
          _id
          name
        }
        facility {
          _id
          name
        }
        ticketType {
          _id
          name
          icon
          color
          isInternal
        }
        assignees {
          _id
        }
        ...TicketScheduleData
      }
    }
  }
  ${Asset.fragments.AssetListData}
  ${Schedule.fragments.TicketScheduleData}
`;

export const GET_SUB_ASSETS_FOR_PROCEDURE_QUERY = gql`
  query getSubAssetsForProcedure($ticketId: ID!) {
    getSubAssetsForProcedure(ticketId: $ticketId) {
      _id
      name
      serialNumber
      image
      thumbnail
      isAccessible
      oem {
        logo
      }
      hierarchy {
        id
        name
      }
      children {
        _id
        name
        serialNumber
        image
        thumbnail
        isAccessible
        oem {
          logo
        }
        hierarchy {
          id
          name
        }
      }
    }
  }
`;

export const LIST_REQUESTABLE_ASSETS_QUERY = gql`
  query listRequestableAssets($params: InputQueryParams) {
    listRequestableAssets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      assets {
        ...AssetListData
        oem {
          ...OemBasicData
        }
      }
    }
  }
  ${Asset.fragments.AssetListData}
  ${Oem.fragments.OemBasicData}
`;

export const LIST_ALL_OWN_OEM_ASSETS_SEARCH_QUERY = gql`
  query listAllOwnOemAssets($params: InputQueryParams) {
    listAllOwnOemAssets(params: $params) {
      currentPage
      limit
      skip
      totalCount
      assets {
        ...AssetSearchListData
      }
    }
  }
  ${Asset.fragments.AssetSearchListData}
`;

export const LIST_OWN_OEM_ASSETS_MISSING_SERIAL_NUMBERS_QUERY = gql`
  query listOwnOemAssetsMissingSerialNumbers($serialNumbers: [String!]!) {
    listOwnOemAssetsMissingSerialNumbers(serialNumbers: $serialNumbers)
  }
`;

export const LIST_OWN_OEM_ASSET_TEMPLATES_MISSING_TEMPLATE_IDS_QUERY = gql`
  query listOwnOemAssetTemplatesMissingTemplateIds($templateIds: [String!]!) {
    listOwnOemAssetTemplatesMissingTemplateIds(templateIds: $templateIds)
  }
`;

export const LIST_OWN_OEM_KANBAN_ASSETS = gql`
  query listOwnOemKanbanAssets($params: InputQueryParams) {
    listOwnOemKanbanAssets(params: $params) {
      totalCount
      columns {
        _id
        boardFor
        type
        customField
        column
        assetType {
          _id
          label
        }
        columnIndex
        cards {
          ...AssetListData
        }
        totalCount
        limit
        skip
        currentPage
      }
    }
  }
  ${Asset.fragments.AssetListData}
`;

export const TRANSLATE_TEXT_QUERY = gql`
  query translateText($input: InputTranslateText!) {
    translateText(input: $input) {
      language
      name
    }
  }
`;

export const LIST_CONTACTS_QUERY = gql`
  query ${LIST_CONTACTS}($params: InputQueryParams!) {
    ${LIST_CONTACTS}(params: $params) {
      totalCount
      skip
      limit
      currentPage
      contacts {
        _id
        name
        email
        deleted
        jobTitle
        landline
        phoneNumber
        oem
        user
        connection {
          _id
          name
        }
        createdBy {
          _id
        }
        accessStatus
        canResendInvite
        customFields {
          fieldId {
            ...CustomAdditionFieldData
          }
          value
          values
        }
      }
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;

export const LIST_CUSTOMER_PORTALS_QUERY = gql`
  query ${LIST_CUSTOMER_PORTALS}($params: InputQueryParams!) {
    ${LIST_CUSTOMER_PORTALS}(params: $params) {
      totalCount
      skip
      limit
      currentPage
      customerPortals {
        _id
        name
        userCount
        createdAt
        childrenCount
        totalPortalCount
        parents {
          id
        }
        connection {
          _id
          name
        }
        createdBy {
          _id
        }
      }
      isExistingContactLinkable
      isConnectionAlreadyEstablished
    }
  }
`;

export const LIST_CONNECTION_REQUESTS_QUERY = gql`
  query ${LIST_CONNECTION_REQUESTS}($params: InputQueryParams!) {
    ${LIST_CONNECTION_REQUESTS}(params: $params) {
      _id
      name
      email
      sentBy {
        _id
        name
        email
      }
      sentTo {
        _id
        name
      }
      sentToUser {
        _id
        name
        email
        oem {
          _id
          name
        }
      }
      existingContactConnection {
        _id
        name
      }
      sentToConnection {
        _id
        name
      }
      sentToContact {
        _id
        name
        email
      }
      connection {
        _id
        name
      }
      sentDate
      status
      senderOem {
        _id
        name
      }
      resolvedBy {
        _id
        name
        email
      }
      resolveDate
      email
      name
      company
    }
  }
`;

export const LIST_DOCUMENTS_QUERY = gql`
  query ${LIST_DOCUMENTS}($params: InputQueryParams!) {
    ${LIST_DOCUMENTS}(params: $params) {
      totalCount
      skip
      limit
      currentPage
      documents {
        ...DocumentData
      }
    }
  }
  ${Document.fragments.DocumentData}
`;

export const GET_DOCUMENT_AUTHORS_QUERY = gql`
  query ${GET_DOCUMENT_AUTHORS} {
    ${GET_DOCUMENT_AUTHORS}
  }
`;

export const GET_DOCUMENT_BY_ID_QUERY = gql`
  query ${GET_DOCUMENT_BY_ID}($documentId: ID!, $pages: [Int], $language: String) {
    ${GET_DOCUMENT_BY_ID}(documentId: $documentId, pages: $pages, language: $language) {
      ...DocumentData
      documentChunks {
        content
        pageContent
        pageNumber
      }
      documentPages {
        _id
        content
        translations {
          content
          language
          status
        }
        pageIndex
      }
    }
  }
  ${Document.fragments.DocumentData}
`;

export const GET_DOCUMENT_CHUNKS_BY_IDS_QUERY = gql`
  query ${GET_DOCUMENT_CHUNKS_BY_IDS}($documentChunkIds: [ID!]!) {
    ${GET_DOCUMENT_CHUNKS_BY_IDS}(documentChunkIds: $documentChunkIds) {
      boxDocumentId
      _id
      content
      distance
      documentPage {
        pageIndex
      }
      documentId
      documentTitle
      documentAITitle
    }
  }
`;

export const QUERY_DOCUMENTS_QUERY = gql`
  query ${QUERY_DOCUMENTS}($query: String!, $activeFilters: JSON) {
    ${QUERY_DOCUMENTS}(query: $query, activeFilters: $activeFilters) {
      groupedResults {
        ...DocumentData
        results {
          _id
          boxDocumentId
          chunkIndex
          documentPage {
            pageIndex
            _id
          }
          distance
          documentId
          documentTitle
          documentAITitle
          pageNumber
        }
      }
      jobId
      queryLanguage
    }
  }
  ${Document.fragments.DocumentData}
`;

export const GET_DOCUMENT_ANSWERS_QUERY = gql`
  query ${GET_DOCUMENT_ANSWERS}($jobId: String!) {
    ${GET_DOCUMENT_ANSWERS}(jobId: $jobId) {
      answer
      documentId
      citations {
        id
        source
        highlight
        pageNumber
        keywords
      }
      pageNumber
      keywords
      highlight
    }
  }
`;

export const GET_DOCUMENT_ANSWER_META_QUERY = gql`
  query ${GET_DOCUMENT_ANSWER_META}($documentChunkIds: [InputChunkWithDistance!]!, $documentId: ID!, $answer: String!, $query: String!) {
    ${GET_DOCUMENT_ANSWER_META}(documentChunkIds: $documentChunkIds, documentId: $documentId, answer: $answer, query: $query) {
      answerWithCitations
      citations {
        id
        source
        pageNumber
        highlight
      }
      pageNumber
      chunkID
    }
  }
`;

export const GET_DOCUMENT_TRANSLATION_HIGHLIGHT_QUERY = gql`
  query ${GET_DOCUMENT_TRANSLATION_HIGHLIGHT}($documentPageContent: String!, $documentPageId: ID!, $documentId: ID!, $translationLanguage: String!) {
    ${GET_DOCUMENT_TRANSLATION_HIGHLIGHT}(documentPageContent: $documentPageContent, documentPageId: $documentPageId, documentId: $documentId, translationLanguage: $translationLanguage)
  }
`;

export const GET_CITATION_HIGHLIGHT_QUERY = gql`
  query ${GET_CITATION_HIGHLIGHT}($documentId: ID!, $documentChunkId: ID!, $question: String!, $answer: String!, $messageId: ID) {
    ${GET_CITATION_HIGHLIGHT}(documentId: $documentId, documentChunkId: $documentChunkId, question: $question, answer: $answer, messageId: $messageId)
  }
`;

export const GET_OWN_CUSTOMER_ASSET_BY_UUID_QUERY = gql`
  query getOwnCustomerAssetByUuid($uuid: ID!) {
    getOwnCustomerAssetByUuid(uuid: $uuid) {
      ...AssetFullData
      isOwnAsset
      isSharedAsset
      isQRCodeFlowEnabled
      sharedAssetAccess {
        documentation
        parts
        preventiveMaintenance
        qrCodes
        history
      }
      oem {
        _id
        name
        slug
        statuses {
          _id
          label
          color
        }
        assetTypes {
          _id
          name
        }
        logo
        thumbnail
        brandLogo
        coverPhoto
        slug
        website
        paidFeatures
      }
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const GET_OWN_CUSTOMER_ASSET_PARTS_BY_UUID_QUERY = gql`
  query getOwnCustomerAssetByUuid($uuid: ID!) {
    getOwnCustomerAssetByUuid(uuid: $uuid) {
      ...AssetPartsData
      isOwnAsset
      isSharedAsset
    }
  }
  ${Asset.fragments.AssetPartsData}
`;

export const BOOK_A_DEMO_QUERY = gql`
  query getBookDemoQuery(
    $companySize: String!
    $product: String!
    $message: String!
  ) {
    getBookDemoQuery(
      companySize: $companySize
      product: $product
      message: $message
    )
  }
`;

export const GET_KNOWLEDGE_BASE_QUERY = gql`
  query ${GET_KNOWLEDGE_BASE}($documentId: ID) {
    ${GET_KNOWLEDGE_BASE}(documentId: $documentId) {
      _id
      supportedLanguages
    }
  }
`;

export const GET_FORM_CONFIG_QUERY = gql`
  query getFormConfig($formKey: String!) {
    getFormConfig(formKey: $formKey) {
      _id
      formKey
      defaultFields {
        fieldKey
        required
      }
      customFields {
        fieldId
        enabled
        required
      }
    }
  }
`;

export const GET_ASSET_3D_INFO_QUERY = gql`
  query getAsset3DInfo($id: ID!) {
    getAsset3DInfo(id: $id) {
      asset3DToken
      _3DViewerURL
    }
  }
`;

const FINANCE_SETTINGS_FIELDS = gql`
  fragment FinanceSettingsFields on FinanceSettings {
    _id
    senderDetails {
      companyName
      streetAddress
      postalCode
      country
      city
    }
    paymentInformation {
      vatId
      taxNumber
      iban
      bicSwift
      bankName
      accountHolderName
      accountNumber
      routingNumber
    }
    taxes {
      _id
      name
      percentage
    }
    documentDefaults {
      paymentTerms
      quotationNumberPrefix
      invoiceNumberPrefix
      invoiceFooterNotes
      quotationFooterNotes
    }
    branding {
      headerImageUrl
      footerImageUrl
    }
    numbering {
      nextQuotationNumber
      nextInvoiceNumber
    }
  }
`;

export const GET_FINANCE_SETTINGS_QUERY = gql`
  ${FINANCE_SETTINGS_FIELDS}
  query getFinanceSettings {
    getFinanceSettings {
      ...FinanceSettingsFields
    }
  }
`;

export const LIST_FINANCE_PACKAGE_TYPES_QUERY = gql`
  query listFinancePackageTypes {
    listFinancePackageTypes {
      _id
      name
    }
  }
`;

export const CHECK_IF_PACKAGES_EXIST_FOR_PACKAGE_TYPE_QUERY = gql`
  query checkIfPackagesExistForPackageType($packageTypeId: ID!) {
    checkIfPackagesExistForPackageType(packageTypeId: $packageTypeId)
  }
`;

export const FINANCE_PACKAGE_FIELDS = gql`
  fragment FinancePackageFields on FinancePackage {
    _id
    name
    description
    packagePrice
    packageUnit
    created_at
    packageType {
      _id
      name
    }
    lineItems {
      _id
      name
      quantity
      unit
      price
    }
  }
`;

export const LIST_FINANCE_PACKAGES_QUERY = gql`
  ${FINANCE_PACKAGE_FIELDS}
  query listFinancePackages {
    listFinancePackages {
      ...FinancePackageFields
    }
  }
`;

export const GET_FINANCE_PACKAGE_QUERY = gql`
  ${FINANCE_PACKAGE_FIELDS}
  query getFinancePackage($id: ID!) {
    getFinancePackage(id: $id) {
      ...FinancePackageFields
    }
  }
`;

const FINANCE_QUOTE_LIST_FIELDS = gql`
  fragment FinanceQuoteListFields on FinanceQuote {
    _id
    quoteNumber
    status
    totalAmount
    validUntilDate
    createdAt
    company {
      _id
      name
      facilityIdentifier
    }
    workOrder {
      _id
      title
      ticketId
    }
    createdBy {
      _id
      name
      email
    }
  }
`;

export const LIST_FINANCE_QUOTES_QUERY = gql`
  ${FINANCE_QUOTE_LIST_FIELDS}
  query listFinanceQuotes($params: InputQueryParams) {
    listFinanceQuotes(params: $params) {
      currentPage
      limit
      skip
      totalCount
      quotes {
        ...FinanceQuoteListFields
      }
    }
  }
`;

export const GET_FINANCE_QUOTE_QUERY = gql`
  ${FINANCE_QUOTE_LIST_FIELDS}
  fragment FinanceQuoteDetailFields on FinanceQuote {
    ...FinanceQuoteListFields
    customerNotes
    internalNotes
    contactEmail
    contact {
      _id
      name
      email
    }
    lineItems {
      _id
      name
      quantity
      unit
      unitPrice
      discountPercent
      markupPercent
      total
      packageId
      packageName
    }
    taxes {
      _id
      taxId
      name
      percentage
    }
    purchaseOrders {
      _id
      name
      url
      type
      size
    }
    linkedInvoices {
      _id
      invoiceNumber
      status
      totalAmount
      issueDate
      createdAt
    }
    sentEmails {
      _id
      subject
      body
      to
      cc
      bcc
      from
      useNoReply
      emailAddressId
      conversationId
      pdfUrl
      sentAt
    }
    publicUrl
  }
  query getFinanceQuote($id: ID!) {
    getFinanceQuote(id: $id) {
      ...FinanceQuoteDetailFields
    }
  }
`;

export const LIST_FINANCE_QUOTE_ACTIVITY_LOGS_QUERY = gql`
  ${ActivityLog.fragments.ActivityLogData}
  query listFinanceQuoteActivityLogs($quoteId: ID!, $params: InputQueryParams) {
    listFinanceQuoteActivityLogs(quoteId: $quoteId, params: $params) {
      totalCount
      limit
      skip
      currentPage
      logs {
        ...ActivityLogData
      }
    }
  }
`;

export const LIST_OEM_EMAIL_THREADS_BY_FINANCE_QUOTE_QUERY = gql`
  query listOemEmailThreadsByFinanceQuote($quoteId: ID!, $emailAddressId: ID!) {
    listOemEmailThreadsByFinanceQuote(
      quoteId: $quoteId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      participants {
        name
        email
      }
    }
  }
`;

const FINANCE_INVOICE_LIST_FIELDS = gql`
  fragment FinanceInvoiceListFields on FinanceInvoice {
    _id
    invoiceNumber
    status
    totalAmount
    issueDate
    dueDate
    createdAt
    company {
      _id
      name
      facilityIdentifier
    }
    workOrder {
      _id
      title
      ticketId
    }
    quote {
      _id
      quoteNumber
    }
    createdBy {
      _id
      name
      email
    }
  }
`;

export const LIST_FINANCE_INVOICES_QUERY = gql`
  ${FINANCE_INVOICE_LIST_FIELDS}
  query listFinanceInvoices($params: InputQueryParams) {
    listFinanceInvoices(params: $params) {
      currentPage
      limit
      skip
      totalCount
      invoices {
        ...FinanceInvoiceListFields
      }
    }
  }
`;

export const GET_FINANCE_INVOICE_QUERY = gql`
  ${FINANCE_INVOICE_LIST_FIELDS}
  fragment FinanceInvoiceDetailFields on FinanceInvoice {
    ...FinanceInvoiceListFields
    customerNotes
    internalNotes
    contactEmail
    contact {
      _id
      name
      email
    }
    quote {
      _id
      quoteNumber
      status
      createdAt
    }
    lineItems {
      _id
      name
      quantity
      unit
      unitPrice
      discountPercent
      markupPercent
      total
      packageId
      packageName
    }
    taxes {
      _id
      taxId
      name
      percentage
    }
    purchaseOrders {
      _id
      name
      url
      type
      size
    }
    sentEmails {
      _id
      subject
      body
      to
      cc
      bcc
      from
      useNoReply
      emailAddressId
      conversationId
      pdfUrl
      sentAt
    }
    publicUrl
  }
  query getFinanceInvoice($id: ID!) {
    getFinanceInvoice(id: $id) {
      ...FinanceInvoiceDetailFields
    }
  }
`;

export const LIST_FINANCE_INVOICE_ACTIVITY_LOGS_QUERY = gql`
  ${ActivityLog.fragments.ActivityLogData}
  query listFinanceInvoiceActivityLogs(
    $invoiceId: ID!
    $params: InputQueryParams
  ) {
    listFinanceInvoiceActivityLogs(invoiceId: $invoiceId, params: $params) {
      totalCount
      limit
      skip
      currentPage
      logs {
        ...ActivityLogData
      }
    }
  }
`;

export const LIST_EMAIL_THREADS_BY_FINANCE_INVOICE_QUERY = gql`
  query listEmailThreadsByFinanceInvoice(
    $scope: String!
    $invoiceId: ID!
    $emailAddressId: ID!
  ) {
    listEmailThreadsByFinanceInvoice(
      scope: $scope
      invoiceId: $invoiceId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      participants {
        name
        email
      }
    }
  }
`;

export const GET_PUBLIC_FINANCE_QUOTE_QUERY = gql`
  query getPublicFinanceQuote($oemSlug: String!, $token: String!) {
    getPublicFinanceQuote(oemSlug: $oemSlug, token: $token) {
      _id
      quoteNumber
      status
      totalAmount
      validUntilDate
      createdAt
      approvedAt
      customerNotes
      oemSlug
      oemName
      oemLogo
      oemCover
      oemCurrency
      contactEmail
      company {
        _id
        name
        website
        address
      }
      createdBy {
        _id
        name
        email
        phone
      }
      contact {
        _id
        name
        email
        phoneNumber
        landline
      }
      senderDetails {
        companyName
        streetAddress
        postalCode
        city
        country
      }
      paymentInformation {
        vatId
        taxNumber
        iban
        bicSwift
        bankName
        accountHolderName
        accountNumber
        routingNumber
      }
      branding {
        headerImageUrl
        footerImageUrl
      }
      documentDefaults {
        paymentTerms
        quotationFooterNotes
      }
      lineItems {
        _id
        name
        quantity
        unit
        unitPrice
        discountPercent
        markupPercent
        total
        packageId
        packageName
      }
      taxes {
        _id
        taxId
        name
        percentage
      }
    }
  }
`;

export const GET_PUBLIC_FINANCE_INVOICE_QUERY = gql`
  query getPublicFinanceInvoice($oemSlug: String!, $token: String!) {
    getPublicFinanceInvoice(oemSlug: $oemSlug, token: $token) {
      _id
      invoiceNumber
      status
      totalAmount
      issueDate
      dueDate
      createdAt
      customerNotes
      oemSlug
      oemName
      oemLogo
      oemCover
      oemCurrency
      contactEmail
      company {
        _id
        name
        website
        address
      }
      createdBy {
        _id
        name
        email
        phone
      }
      contact {
        _id
        name
        email
        phoneNumber
        landline
      }
      senderDetails {
        companyName
        streetAddress
        postalCode
        city
        country
      }
      paymentInformation {
        vatId
        taxNumber
        iban
        bicSwift
        bankName
        accountHolderName
        accountNumber
        routingNumber
      }
      branding {
        headerImageUrl
        footerImageUrl
      }
      documentDefaults {
        paymentTerms
        invoiceFooterNotes
      }
      lineItems {
        _id
        name
        quantity
        unit
        discountPercent
        markupPercent
        unitPrice
        total
      }
      taxes {
        _id
        name
        percentage
      }
    }
  }
`;

export const AUTOMATION_FIELDS = gql`
  fragment AutomationFields on Automation {
    _id
    name
    description
    status
    runCount
    lastRunAt
    updated_at
    created_at
    trigger {
      module
      event
      statuses
    }
    conditions {
      field
      operator
      values
    }
    action {
      type
      userIds
    }
  }
`;

/** Internal OEM users eligible as automation notification recipients (no facility). */
export const LIST_AUTOMATION_NOTIFICATION_USERS_QUERY = gql`
  query listAutomationNotificationUsers($getAllUsers: Boolean) {
    listOwnOemSupportAccounts(getAllUsers: $getAllUsers) {
      _id
      name
      email
      role
      facility {
        _id
      }
    }
  }
`;

export const LIST_AUTOMATIONS_QUERY = gql`
  ${AUTOMATION_FIELDS}
  query listAutomations {
    listAutomations {
      ...AutomationFields
    }
  }
`;

export const GET_AUTOMATION_QUERY = gql`
  ${AUTOMATION_FIELDS}
  query getAutomation($id: ID!) {
    getAutomation(id: $id) {
      ...AutomationFields
    }
  }
`;
